/**
 * The start view's editable combobox (Option C, C1, plan §3.6; WAI-ARIA combobox with a listbox popup that is always
 * shown). Focus stays in the filter; `aria-activedescendant` names the active option.
 *
 * - Down / Up move to the next / previous VISIBLE option in logical order (Databases → Azure services → Other ways to
 *   add), never by grid geometry, so every option is reachable at any column count. From no active option Down goes to
 *   the first and Up to the last; the ends do not wrap.
 * - Left / Right, Home / End and Space are always text-editing keys (a departure from the prototype, Codex r2 point 8).
 * - Enter activates the active option; with none (no key pressed yet) it does nothing — there is no hidden default.
 * - Typing makes the first matching engine active (none when nothing matches); clearing the filter keeps a still-visible
 *   active option. Navigation and Enter are ignored while an IME composes.
 * - C3: while a detection card replaces the list (the filter holds a connection string) there is no option: no active
 *   descendant, Up / Down do nothing, and Enter is Continue.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { firstEngineOption, startOptionOrder, type StartOptionId } from './engineFilter';

export function startOptionDomId(id: StartOptionId): string {
  return `connection-start-option-${id.replace(':', '-')}`;
}

type Options = {
  filter: string;
  /** Returning from the form: the engine the user left is the active option. */
  initialActive?: StartOptionId | null;
  onActivate: (id: StartOptionId, via: 'enter' | 'click') => void;
  /** Continue on the detection card that replaces the list; undefined while no card shows. */
  onContinue?: () => void;
};

export function nextActiveOption(order: readonly StartOptionId[], active: StartOptionId | null, direction: 'down' | 'up'): StartOptionId | null {
  if (order.length === 0) return null;
  const index = active ? order.indexOf(active) : -1;
  if (index < 0) return direction === 'down' ? order[0] : order[order.length - 1];
  const next = direction === 'down' ? Math.min(order.length - 1, index + 1) : Math.max(0, index - 1);
  return order[next];
}

/** The active option after the filter changed: typing picks the first match; clearing keeps a visible one. */
export function activeAfterFilter(filter: string, active: StartOptionId | null): StartOptionId | null {
  if (filter.trim()) return firstEngineOption(filter);
  return active && startOptionOrder(filter).includes(active) ? active : null;
}

function isComposing(event: KeyboardEvent): boolean {
  return event.nativeEvent.isComposing || event.keyCode === 229;
}

export function useEngineCombobox({ filter, initialActive = null, onActivate, onContinue }: Options) {
  const [active, setActive] = useState<StartOptionId | null>(initialActive);
  const previousFilter = useRef(filter);

  useEffect(() => {
    if (previousFilter.current === filter) return;
    previousFilter.current = filter;
    setActive((current) => activeAfterFilter(filter, current));
  }, [filter]);

  // Keep the active option in view (the list scrolls, the filter row stays).
  useEffect(() => {
    if (!active) return;
    document.getElementById(startOptionDomId(active))?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey || isComposing(event)) return;
    if (onContinue) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter') event.preventDefault();
      if (event.key === 'Enter') onContinue();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => nextActiveOption(startOptionOrder(filter), current, event.key === 'ArrowDown' ? 'down' : 'up'));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (active && startOptionOrder(filter).includes(active)) onActivate(active, 'enter');
    }
  };

  return {
    active,
    setActive,
    activeDescendant: active && !onContinue ? startOptionDomId(active) : undefined,
    onKeyDown,
  };
}
